import { useEffect, useState } from 'react';
import { constantFormValues } from '../../root';
import { NumericFormat } from 'react-number-format';
import { withValueCap } from 'pages/globals/global';
import { processArrays } from '../LeaseUtils/helper';
import AddIconButton from 'components/button/AddIconButton';
import DeleteIconButton from 'components/button/DeleteIconButton';
import { Button, Form, Input, message, Modal, Tooltip } from 'antd';
import AdvanceAmountHistory from '../LeaseUtils/History/AdvanceAmountHistory';
import { Table, Stack, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from '@mui/material';

const AdvanceAmount = ({ screen = 'CREATE', dataFullyLoaded = false }) => {
	const form = Form.useFormInstance();
	const advanceAmountListSet = form.getFieldValue('advanceAmountListSet');

	const [rowTotals, setRowTotals] = useState([]);
	const [totalAmount, setTotalAmount] = useState(0);
	const [openModal, setOpenModal] = useState(false);

	const advanceAmountListSetForHistory = advanceAmountListSet?.length > 1 ? [...advanceAmountListSet]?.slice(0, -1) : [];

	const callDebounced = () => {
		if (screen === 'EDIT' && window.debouncedManageChangeRows) {
			window.debouncedManageChangeRows();
		}
	};

	const formAdvanceAmount = Form.useWatch(constantFormValues?.advanceAmount?.key, form);

	useEffect(() => {
		if (formAdvanceAmount !== undefined && formAdvanceAmount !== null) {
			if (formAdvanceAmount !== totalAmount) {
				setTotalAmount(formAdvanceAmount);
			}
		}
	}, [formAdvanceAmount]);

	useEffect(() => {
		if (totalAmount > 0) {
			const currentFormValue = form.getFieldValue(constantFormValues?.advanceAmount?.key);

			if (currentFormValue !== totalAmount) {
				form.setFieldValue(constantFormValues?.advanceAmount?.key, totalAmount);
			}
		}
	}, [totalAmount]);

	function transformRangeDataToAmountArray(rangeData) {
		const result = [];

		rangeData.forEach((range) => {
			const fromValue = parseInt(range.from);
			const toValue = parseInt(range.to);

			if (isNaN(fromValue) || isNaN(toValue) || fromValue > toValue) {
				console.warn('Invalid range found:', range);
				return;
			}

			const amount = parseFloat(String(range.amount).replace(/,/g, ''));

			if (isNaN(amount)) {
				console.warn('Invalid amount found:', range);
				return;
			}

			const repetitions = toValue - fromValue + 1;

			for (let i = 0; i < repetitions; i++) {
				result.push(amount);
			}
		});

		return result;
	}

	const calculateRowTotals = () => {
		try {
			const values = form.getFieldValue('advanceAmountList');

			if (values && Array.isArray(values)) {
				const newRowTotals = values.map((item) => {
					if (!item) return 0;

					const fromValue = parseInt(item.from);
					const toValue = parseInt(item.to);

					let amount = 0;

					if (item.amount) {
						const amountStr = String(item.amount).replace(/,/g, '');
						amount = parseFloat(amountStr);
					}

					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
						return 0;
					}

					return (toValue - fromValue + 1) * amount;
				});

				setRowTotals(newRowTotals);
			}
		} catch (error) {
			console.error('Error calculating row totals:', error);
		}
	};

	const calculateTotal = () => {
		try {
			const values = form.getFieldValue('advanceAmountList');

			if (values && Array.isArray(values)) {
				const currentRowTotals = values.map((item) => {
					if (!item) return 0;

					const fromValue = parseInt(item.from);
					const toValue = parseInt(item.to);

					let amount = 0;

					if (item.amount !== undefined && item.amount !== null && item.amount !== '') {
						const amountStr = String(item.amount).replace(/,/g, '');
						amount = parseFloat(amountStr);
					}

					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
						return 0;
					}

					return (toValue - fromValue + 1) * amount;
				});

				setRowTotals(currentRowTotals);

				const sum = currentRowTotals.reduce((total, rowTotal) => total + (rowTotal || 0), 0);

				setTotalAmount(sum);
			}
		} catch (error) {
			console.error('Error calculating total:', error);
		}
	};

	const handleAmountChange = () => {
		setTimeout(() => {
			calculateTotal();
			handleApply();
		}, 10);
	};

	const handleToChange = () => {
		setTimeout(() => {
			calculateRowTotals();
			handleApply();
		}, 10);
	};

	const validateToValueOnBlur = (index) => {
		const currentValues = form.getFieldValue('advanceAmountList');
		if (!currentValues) return;

		const toValue = parseInt(currentValues[index].to);
		const fromValue = parseInt(currentValues[index].from);

		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
			message.error("'To' value must be greater than 'From' value");
			form.setFields([
				{
					name: ['advanceAmountList', index, 'to'],
					value: '',
					errors: ["'To' value must be greater than 'From' value"],
				},
			]);
		}

		calculateRowTotals();
		handleApply();
	};

	const isRowValid = (row) => {
		if (!row) return false;

		const toValue = parseInt(row.to);
		const fromValue = parseInt(row.from);

		if (isNaN(toValue) || toValue <= 0) return false;
		if (isNaN(fromValue) || fromValue <= 0) return false;
		if (toValue < fromValue) return false;
		if (!row.amount) return false;

		return true;
	};

	const handleAddRow = (index) => {
		const leaseType = form.getFieldValue('leaseType');
		const currentValues = form.getFieldValue('advanceAmountList');

		if (!currentValues || !currentValues.length) {
			form.setFieldValue('advanceAmountList', [{ from: 1, to: '', amount: '', totalAmount: '' }]);
			return;
		}

		const currentRow = currentValues[index];

		if (!isRowValid(currentRow)) {
			message.error('Please complete all required fields in the current row before adding a new one');
			return;
		}

		const toValue = parseInt(currentRow.to);

		if (leaseType === 'Operating Lease' && toValue >= 12) {
			message.error('Maximum periods taken');
			return;
		}

		const newFrom = toValue + 1;

		const newRow = { from: newFrom, to: '', amount: '', totalAmount: '' };

		const updatedValues = [...currentValues.slice(0, index + 1), newRow, ...currentValues.slice(index + 1)];

		for (let i = index + 2; i < updatedValues.length; i++) {
			const prevTo = parseInt(updatedValues[i - 1].to);
			if (!isNaN(prevTo)) {
				updatedValues[i].from = prevTo + 1;
			}
		}

		form.setFieldValue('advanceAmountList', updatedValues);

		setTimeout(handleApply, 10);
	};
	useEffect(() => {
		if (screen === 'VIEW' && dataFullyLoaded) {
			const timeoutId = setTimeout(() => {
				const currentValues = form.getFieldValue('advanceAmountList');

				if (currentValues && currentValues.length > 0) {
					calculateTotal();
				}
			}, 700);

			return () => clearTimeout(timeoutId);
		}
	}, [screen, dataFullyLoaded]);

	const handleApply = () => {
		const currentValues = form.getFieldValue('advanceAmountList');
		if (!currentValues || currentValues?.length === 0) return;

		const transformedData = transformRangeDataToAmountArray(currentValues);
		const grossRents = form.getFieldValue('grossRents');
		const { processedAdvanceAmounts } = processArrays(grossRents, transformedData);

		const total = transformedData.reduce((sum, amount) => sum + amount, 0);

		form.setFieldValue('advanceAmounts', processedAdvanceAmounts);

		setTotalAmount(total);

		form.setFieldsValue({
			advanceAmount: total,
			advanceAmounts: processedAdvanceAmounts,
		});

		form.validateFields(['advanceAmounts', 'advanceAmount']);

		callDebounced();
	};

	useEffect(() => {
		const currentValues = form.getFieldValue('advanceAmountList');
		if (screen === 'VIEW') {
			return;
		}
		if (!currentValues || !currentValues.length) {
			form.setFieldValue('advanceAmountList', [{ from: 1, to: '', amount: '', totalAmount: '' }]);
		}

		calculateTotal();
		calculateRowTotals();
	}, []);

	useEffect(() => {
		if (totalAmount > 0) {
			form.setFieldValue(constantFormValues?.advanceAmount?.key, totalAmount);

			const currentChanges = form.getFieldValue('changes');

			if (currentChanges && currentChanges.length > 0) {
				const updatedChanges = [...currentChanges];

				updatedChanges[0] = {
					...updatedChanges[0],
					inputChangeAdvance: totalAmount,
				};

				form.setFieldValue('changes', updatedChanges);
			}
		}
	}, [totalAmount]);

	return (
		<div>
			<Form.List name={'advanceAmountList'}>
				{(fields) => {
					return (
						<>
							<TableContainer
								className="gross-rent-table"
								sx={{
									'& table, & th, & td': {
										border: '1px solid #e0e0e0',
										borderCollapse: 'collapse',
									},
									'& th, & td': {
										p: '5px',
									},
									'& td .ant-form-item': {
										marginBottom: '0px',
									},

									'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' },
									'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' },
									'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' },
									'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' },
									'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' },
									'& table': { width: '100%' },
								}}
							>
								<Table aria-label="Gross Rent table">
									<TableHead>
										<TableRow>
											<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
												From Month
											</TableCell>
											<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>To Month</TableCell>
											<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
												Monthly Adjustment
											</TableCell>
											{screen !== 'VIEW' && screen !== 'EDIT' && (
												<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
													Action
												</TableCell>
											)}
										</TableRow>
									</TableHead>
									<TableBody>
										{fields.length > 0 ? (
											fields.map((field, index) => (
												<TableRow key={field.key}>
													<TableCell>
														<Form.Item name={[field.name, 'from']}>
															<Input
																disabled={true}
																style={{
																	width: '100%',
																	textAlign: 'center',
																	padding: '5px 11px',
																	backgroundColor: '#f5f5f5',
																}}
															/>
														</Form.Item>
													</TableCell>

													<TableCell>
														<Form.Item
															name={[field.name, 'to']}
															rules={[
																{ required: false, message: "Please enter a 'To' value" },
																({ getFieldValue }) => ({
																	validator(_, value) {
																		const leaseType = getFieldValue('leaseType');
																		const fromValue = getFieldValue(['advanceAmountList', field.name, 'from']);
																		if (!value || !fromValue) return Promise.resolve();
																		if (parseInt(value) < parseInt(fromValue)) {
																			return Promise.reject(new Error("'To' must be greater than 'From'"));
																		}
																		if (leaseType === 'Operating Lease' && parseInt(value) > 12) {
																			return Promise.reject(new Error('Maximum month is 12'));
																		}
																		return Promise.resolve();
																	},
																}),
															]}
														>
															<Input
																type="number"
																disabled={screen === 'VIEW' || screen === 'EDIT'}
																placeholder="Enter To value"
																style={{
																	padding: '5px 11px',
																	width: '100%',

																	textAlign: 'center',
																}}
																onChange={() => handleToChange(`${'advanceAmountList'}.${field.name}.to`)}
																onBlur={() => validateToValueOnBlur(index)}
															/>
														</Form.Item>
													</TableCell>
													<TableCell>
														<Form.Item
															name={[field.name, 'amount']}
															rules={[{ required: false, message: 'Please enter an amount' }]}
														>
															<NumericFormat
																size="small"
																decimalScale={2}
																placeholder="0.00"
																fixedDecimalScale
																thousandSeparator=","
																isAllowed={withValueCap}
																style={{
																	padding: '5px 11px',
																	textAlign: 'right',
																	height: '32px',
																	borderRadius: '4px',
																	fontSize: '13px',
																}}
																customInput={Input}
																disabled={screen === 'VIEW' || screen === 'EDIT'}
																onValueChange={(values) => {
																	handleAmountChange(`${'advanceAmountList'}.${field.name}.amount`);

																	const currentValues = [...form.getFieldValue('advanceAmountList')];

																	if (currentValues[index]) {
																		currentValues[index].amount = values.value || '';

																		form.setFieldValue('advanceAmountList', currentValues);

																		const fromValue = parseInt(currentValues[index].from);
																		const toValue = parseInt(currentValues[index].to);

																		if (!values.value) {
																			const newRowTotals = [...rowTotals];
																			newRowTotals[index] = 0;
																			setRowTotals(newRowTotals);

																			const newTotal = newRowTotals.reduce(
																				(total, rowTotal) => total + (rowTotal || 0),
																				0
																			);
																			setTotalAmount(newTotal);
																			return;
																		}

																		const amount = parseFloat(values.value);

																		if (
																			!isNaN(fromValue) &&
																			!isNaN(toValue) &&
																			!isNaN(amount) &&
																			toValue >= fromValue
																		) {
																			const newRowTotals = [...rowTotals];
																			newRowTotals[index] = (toValue - fromValue + 1) * amount;
																			setRowTotals(newRowTotals);

																			const newTotal = newRowTotals.reduce(
																				(total, rowTotal) => total + (rowTotal || 0),
																				0
																			);
																			setTotalAmount(newTotal);
																		}
																	}
																}}
																onBlur={() => {
																	setTimeout(() => {
																		calculateRowTotals();
																		calculateTotal();
																		handleApply();
																	}, 10);
																}}
															/>
														</Form.Item>
													</TableCell>

													{screen !== 'VIEW' && screen !== 'EDIT' && (
														<TableCell align="center">
															<Stack sx={{ flexDirection: 'row', justifyContent: 'center' }}>
																{fields.length === 1 && index === 0 && (
																	<Tooltip title="Add Row">
																		<AddIconButton onClick={() => handleAddRow(index)} />
																	</Tooltip>
																)}

																{fields.length > 1 && index > 0 && index < fields.length - 1 && (
																	<Tooltip title="Delete Row">
																		<DeleteIconButton
																			onClick={() => {
																				const currentValues = [...form.getFieldValue('advanceAmountList')];

																				const newRowTotals = [...rowTotals];
																				newRowTotals.splice(index, 1);
																				setRowTotals(newRowTotals);

																				currentValues.splice(index, 1);

																				for (let i = index; i < currentValues.length; i++) {
																					if (i > 0) {
																						const prevTo = parseInt(currentValues[i - 1].to);
																						if (!isNaN(prevTo)) {
																							currentValues[i].from = prevTo + 1;
																						}
																					}
																				}

																				form.setFieldValue('advanceAmountList', currentValues);

																				const newTotal = newRowTotals.reduce(
																					(total, rowTotal) => total + (rowTotal || 0),
																					0
																				);
																				setTotalAmount(newTotal);
																				handleApply();
																			}}
																		/>
																	</Tooltip>
																)}

																{fields.length > 1 && index === fields.length - 1 && (
																	<>
																		<Tooltip title="Add Row">
																			<AddIconButton onClick={() => handleAddRow(index)} />
																		</Tooltip>

																		<Tooltip title="Delete Row">
																			<DeleteIconButton
																				onClick={() => {
																					const currentValues = [
																						...form.getFieldValue('advanceAmountList'),
																					];

																					const newRowTotals = [...rowTotals];
																					newRowTotals.splice(index, 1);
																					setRowTotals(newRowTotals);

																					currentValues.splice(index, 1);

																					for (let i = index; i < currentValues.length; i++) {
																						if (i > 0) {
																							const prevTo = parseInt(currentValues[i - 1].to);
																							if (!isNaN(prevTo)) {
																								currentValues[i].from = prevTo + 1;
																							}
																						}
																					}

																					form.setFieldValue('advanceAmountList', currentValues);

																					const newTotal = newRowTotals.reduce(
																						(total, rowTotal) => total + (rowTotal || 0),
																						0
																					);
																					setTotalAmount(newTotal);
																					handleApply();
																				}}
																			/>
																		</Tooltip>
																	</>
																)}

																{fields.length > 1 && index === 0 && <span></span>}
															</Stack>
														</TableCell>
													)}
												</TableRow>
											))
										) : (
											<TableRow>
												<TableCell colSpan={5} style={{ textAlign: 'center' }}>
													No data. Click the plus icon to add new entries.
												</TableCell>
											</TableRow>
										)}
									</TableBody>

									<TableFooter>
										<TableRow sx={{ height: 24 }}>
											<TableCell
												colSpan={2}
												align="right"
												sx={{ fontWeight: 'bold', fontSize: '13px', backgroundColor: '#f6f5f5', pr: '14px' }}
											>
												Advance Payment
											</TableCell>

											<TableCell style={{ fontWeight: 'bold', textAlign: 'right' }}>
												<NumericFormat
													value={totalAmount || 0}
													displayType="input"
													decimalScale={2}
													fixedDecimalScale
													thousandSeparator=","
													size="small"
													style={{
														padding: '5px 11px',
														textAlign: 'right',
														height: '32px',
														borderRadius: '4px',
														fontSize: '13px',
														fontWeight: 'bold',
														backgroundColor: undefined,
														borderColor: undefined,
													}}
													customInput={Input}
													disabled={true}
													readOnly={true}
												/>
											</TableCell>
											{screen !== 'VIEW' && screen !== 'EDIT' && <TableCell colSpan={1}></TableCell>}
										</TableRow>
									</TableFooter>
								</Table>
							</TableContainer>

							<Form.Item name="advanceAmounts" noStyle />
						</>
					);
				}}
			</Form.List>

			<Modal
				title={'Advance Amount Modifications'}
				open={openModal}
				onCancel={() => setOpenModal(false)}
				width={800}
				footer={[
					<Button key="cancel" onClick={() => setOpenModal(false)}>
						Close
					</Button>,
				]}
			>
				{advanceAmountListSetForHistory?.map((item, index) => (
					<AdvanceAmountHistory key={index} data={item} index={index} />
				))}
			</Modal>
		</div>
	);
};

export default AdvanceAmount;

// import React, { useEffect, useState } from 'react';
// import { Button, Form, Input, message, Modal, Tooltip } from 'antd';
// import { NumericFormat } from 'react-number-format';
// import { withValueCap } from 'pages/globals/global';
// import { Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from '@mui/material';
// import InputLabel from 'components/InputLabel/index';
// import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';

// import { constantFormValues } from '../../root';
// // import { debounce } from 'lodash';
// import AdvanceAmountHistory from '../LeaseUtils/History/AdvanceAmountHistory';
// import { processArrays } from '../LeaseUtils/helper';

// const AdvanceAmount = ({ screen = 'CREATE', dataFullyLoaded = false }) => {
// 	const form = Form.useFormInstance();
// 	const advanceAmountListSet = form.getFieldValue('advanceAmountListSet');
// 	// State to track the total amount
// 	const [totalAmount, setTotalAmount] = useState(0);
// 	const [openModal, setOpenModal] = useState(false);
// 	const advanceAmountListSetForHistory = advanceAmountListSet?.length > 1 ? [...advanceAmountListSet]?.slice(0, -1) : [];
// 	// State to track which values have been modified (for highlighting)
// 	// const [modifiedFields, setModifiedFields] = useState({});
// 	// State to track row totals
// 	const [rowTotals, setRowTotals] = useState([]);

// 	// Call debouncedManageChangeRows when the component's values change
// 	const callDebounced = () => {
// 		if (screen === 'EDIT' && window.debouncedManageChangeRows) {
// 			window.debouncedManageChangeRows();
// 		}
// 	};

// 	// Use Form.useWatch to monitor the advanceAmount value directly from the form
// 	const formAdvanceAmount = Form.useWatch(constantFormValues?.advanceAmount?.key, form);
// 	// const handleHistory = () => {
// 	// 	setOpenModal(true);
// 	// };
// 	// Add this useEffect to debug form value changes
// 	useEffect(() => {
// 		if (formAdvanceAmount !== undefined && formAdvanceAmount !== null) {
// 			// If the form value exists but differs from our state, update our state
// 			if (formAdvanceAmount !== totalAmount) {
// 				setTotalAmount(formAdvanceAmount);
// 			}
// 		}
// 	}, [formAdvanceAmount]);

// 	// Add this useEffect to prevent the form from overriding our calculated total
// 	useEffect(() => {
// 		// When totalAmount changes, make sure the form value is updated
// 		if (totalAmount > 0) {
// 			const currentFormValue = form.getFieldValue(constantFormValues?.advanceAmount?.key);

// 			// If the form value differs from our calculated total, update it
// 			if (currentFormValue !== totalAmount) {
// 				form.setFieldValue(constantFormValues?.advanceAmount?.key, totalAmount);
// 			}
// 		}
// 	}, [totalAmount]);
// 	function transformRangeDataToAmountArray(rangeData) {
// 		// Initialize an empty result array
// 		const result = [];

// 		// Process each range object
// 		rangeData.forEach((range) => {
// 			const fromValue = parseInt(range.from);
// 			const toValue = parseInt(range.to);

// 			// Skip invalid ranges
// 			if (isNaN(fromValue) || isNaN(toValue) || fromValue > toValue) {
// 				console.warn('Invalid range found:', range);
// 				return;
// 			}

// 			// Convert amount string to number (remove commas)
// 			const amount = parseFloat(String(range.amount).replace(/,/g, ''));

// 			// Skip if amount is not a valid number
// 			if (isNaN(amount)) {
// 				console.warn('Invalid amount found:', range);
// 				return;
// 			}

// 			// Calculate how many times to repeat the amount ((to - from) + 1)
// 			const repetitions = toValue - fromValue + 1;

// 			// Add the amount to the result array the specified number of times
// 			for (let i = 0; i < repetitions; i++) {
// 				result.push(amount);
// 			}
// 		});

// 		return result;
// 	}

// 	// Calculate row totals based on current form values
// 	const calculateRowTotals = () => {
// 		// if (!form || !formListName) return;

// 		try {
// 			// Get the current values from the form
// 			const values = form.getFieldValue('advanceAmountList');

// 			if (values && Array.isArray(values)) {
// 				const newRowTotals = values.map((item) => {
// 					if (!item) return 0;

// 					const fromValue = parseInt(item.from);
// 					const toValue = parseInt(item.to);

// 					// If amount exists, convert it from string to number
// 					let amount = 0;
// 					if (item.amount) {
// 						const amountStr = String(item.amount).replace(/,/g, '');
// 						amount = parseFloat(amountStr);
// 					}

// 					// Skip invalid values
// 					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
// 						return 0;
// 					}

// 					// Calculate total: (to - from + 1) * amount
// 					return (toValue - fromValue + 1) * amount;
// 				});

// 				setRowTotals(newRowTotals);
// 			}
// 		} catch (error) {
// 			console.error('Error calculating row totals:', error);
// 		}
// 	};

// 	const calculateTotal = () => {
// 		try {
// 			// Get the current values from the form
// 			const values = form.getFieldValue('advanceAmountList');

// 			if (values && Array.isArray(values)) {
// 				// Calculate row totals directly without waiting for state update
// 				const currentRowTotals = values.map((item) => {
// 					if (!item) return 0;

// 					const fromValue = parseInt(item.from);
// 					const toValue = parseInt(item.to);

// 					// If amount exists, convert it from string to number
// 					let amount = 0;
// 					if (item.amount !== undefined && item.amount !== null && item.amount !== '') {
// 						const amountStr = String(item.amount).replace(/,/g, '');
// 						amount = parseFloat(amountStr);
// 					}

// 					// Skip invalid values
// 					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
// 						return 0;
// 					}

// 					// Calculate total: (to - from + 1) * amount
// 					return (toValue - fromValue + 1) * amount;
// 				});

// 				// Update row totals state
// 				setRowTotals(currentRowTotals);

// 				// Now calculate the grand total using the current row totals
// 				const sum = currentRowTotals.reduce((total, rowTotal) => total + (rowTotal || 0), 0);

// 				// Update total amount state
// 				setTotalAmount(sum);
// 			}
// 		} catch (error) {
// 			console.error('Error calculating total:', error);
// 		}
// 	};

// 	// Handler for when an amount changes
// 	const handleAmountChange = () => {
// 		// Mark this field as modified for highlighting
// 		// setModifiedFields((prev) => ({
// 		// 	...prev,
// 		// 	[fieldName]: true,
// 		// }));

// 		// Recalculate the total
// 		setTimeout(() => {
// 			calculateTotal();
// 			handleApply();
// 		}, 10);
// 	};

// 	// Handler for "To" value changes
// 	const handleToChange = () => {
// 		// Only mark this field as modified for highlighting
// 		// setModifiedFields((prev) => ({
// 		// 	...prev,
// 		// 	[fieldName]: true,
// 		// }));
// 		// Calculate row totals when "To" value changes
// 		setTimeout(() => {
// 			calculateRowTotals();
// 			handleApply();
// 		}, 10);
// 	};

// 	// Validate To value ONLY on blur to allow typing multi-digit numbers
// 	const validateToValueOnBlur = (index) => {
// 		// Get the current values
// 		const currentValues = form.getFieldValue('advanceAmountList');
// 		if (!currentValues) return;

// 		const toValue = parseInt(currentValues[index].to);
// 		const fromValue = parseInt(currentValues[index].from);

// 		// Validate that To is greater than From
// 		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
// 			message.error("'To' value must be greater than 'From' value");
// 			form.setFields([
// 				{
// 					name: ['advanceAmountList', index, 'to'],
// 					value: '',
// 					errors: ["'To' value must be greater than 'From' value"],
// 				},
// 			]);
// 		}
// 		// Recalculate row totals after validation
// 		calculateRowTotals();
// 		handleApply();
// 	};

// 	// Validate if a row is complete and valid
// 	const isRowValid = (row) => {
// 		if (!row) return false;

// 		// Check if all required fields are filled
// 		const toValue = parseInt(row.to);
// 		const fromValue = parseInt(row.from);

// 		if (isNaN(toValue) || toValue <= 0) return false;
// 		if (isNaN(fromValue) || fromValue <= 0) return false;
// 		if (toValue < fromValue) return false;
// 		if (!row.amount) return false;

// 		return true;
// 	};

// 	// Handle adding a new row - modified to add after a specific row
// 	const handleAddRow = (index) => {
// 		const leaseType = form.getFieldValue('leaseType');
// 		const currentValues = form.getFieldValue('advanceAmountList');

// 		// Initialize with first row if no values
// 		if (!currentValues || !currentValues.length) {
// 			form.setFieldValue('advanceAmountList', [{ from: 1, to: '', amount: '', totalAmount: '' }]);
// 			return;
// 		}

// 		// Get the row to validate
// 		const currentRow = currentValues[index];

// 		// Check if the current row is complete
// 		if (!isRowValid(currentRow)) {
// 			message.error('Please complete all required fields in the current row before adding a new one');
// 			return;
// 		}

// 		// Get the "to" value from the current row
// 		const toValue = parseInt(currentRow.to);

// 		if (leaseType === 'Operating Lease' && toValue >= 12) {
// 			message.error('Maximum periods taken');
// 			return;
// 		}

// 		// New row's "From" value is current row's "To" value + 1
// 		const newFrom = toValue + 1;

// 		// Create the new row
// 		const newRow = { from: newFrom, to: '', amount: '', totalAmount: '' };

// 		// Insert the new row after the current one
// 		const updatedValues = [...currentValues.slice(0, index + 1), newRow, ...currentValues.slice(index + 1)];

// 		// Fix the From values for all subsequent rows
// 		for (let i = index + 2; i < updatedValues.length; i++) {
// 			const prevTo = parseInt(updatedValues[i - 1].to);
// 			if (!isNaN(prevTo)) {
// 				updatedValues[i].from = prevTo + 1;
// 			}
// 		}

// 		form.setFieldValue('advanceAmountList', updatedValues);

// 		// Automatically apply the changes
// 		setTimeout(handleApply, 10);
// 	};
// 	useEffect(() => {
// 		if (screen === 'VIEW' && dataFullyLoaded) {
// 			// Add a larger delay to ensure form data is populated in view mode
// 			const timeoutId = setTimeout(() => {
// 				const currentValues = form.getFieldValue('advanceAmountList');

// 				if (currentValues && currentValues.length > 0) {
// 					calculateTotal(); // Use this instead of separate calls
// 				}
// 			}, 700); // Increased from 500ms

// 			return () => clearTimeout(timeoutId);
// 		}
// 	}, [screen, dataFullyLoaded]);

// 	const handleApply = () => {
// 		const currentValues = form.getFieldValue('advanceAmountList');
// 		if (!currentValues || currentValues?.length === 0) return;

// 		const transformedData = transformRangeDataToAmountArray(currentValues);
// 		const grossRents = form.getFieldValue('grossRents');
// 		const { processedAdvanceAmounts } = processArrays(grossRents, transformedData);

// 		const total = transformedData.reduce((sum, amount) => sum + amount, 0);

// 		// First set the advanceAmounts array for API submission
// 		form.setFieldValue('advanceAmounts', processedAdvanceAmounts);

// 		// Then update local state
// 		setTotalAmount(total);

// 		// Use a more direct approach to update the form and trigger change detection
// 		form.setFieldsValue({
// 			advanceAmount: total,
// 			advanceAmounts: processedAdvanceAmounts,
// 		});

// 		// Force the form to validate and notify watchers
// 		form.validateFields(['advanceAmounts', 'advanceAmount']);

// 		// Call debouncedManageChangeRows when data changes in Edit mode
// 		callDebounced();
// 	};

// 	// Initialize with the first row
// 	useEffect(() => {
// 		const currentValues = form.getFieldValue('advanceAmountList');
// 		if (screen === 'VIEW') {
// 			return;
// 		}
// 		if (!currentValues || !currentValues.length) {
// 			// Initialize with one row, from=1
// 			form.setFieldValue('advanceAmountList', [{ from: 1, to: '', amount: '', totalAmount: '' }]);
// 		}

// 		// Calculate initial total if data exists
// 		calculateTotal();
// 		calculateRowTotals();
// 	}, []);

// 	// Ensure advanceAmount in the form stays synchronized with the total
// 	useEffect(() => {
// 		// Only update the form when the total actually changes
// 		if (totalAmount > 0) {
// 			form.setFieldValue(constantFormValues?.advanceAmount?.key, totalAmount);

// 			// Also update changes array if it exists
// 			const currentChanges = form.getFieldValue('changes');
// 			if (currentChanges && currentChanges.length > 0) {
// 				// Make a shallow copy to avoid direct state mutation
// 				const updatedChanges = [...currentChanges];
// 				updatedChanges[0] = {
// 					...updatedChanges[0],
// 					inputChangeAdvance: totalAmount,
// 				};
// 				form.setFieldValue('changes', updatedChanges);
// 			}

// 			// console.log('Synchronized advanceAmount with total:', totalAmount);
// 		}
// 	}, [totalAmount]);

// 	return (
// 		<div>
// 			<Form.List name={'advanceAmountList'}>
// 				{(fields) => {
// 					return (
// 						<>
// 							<TableContainer
// 								className="gross-rent-table"
// 								sx={{
// 									'& table, & th, & td': {
// 										border: '1px solid #e0e0e0',
// 										borderCollapse: 'collapse',
// 									},
// 									'& th, & td': {
// 										p: '5px',
// 									},
// 									'& td .ant-form-item': {
// 										marginBottom: '0px',
// 									},

// 									'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' } /* From/From Month */,
// 									'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' } /* To/To Month */,
// 									'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' } /* Amount/Monthly Adjustment */,
// 									'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' } /* Remarks/Total Amount */,
// 									'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' } /* Action */,
// 									'& table': { width: '100%' } /* Ensure table takes full container width */,
// 								}}
// 							>
// 								<Table aria-label="Gross Rent table">
// 									<TableHead>
// 										<TableRow>
// 											<TableCell sx={{ textAlign: 'center' }}>From Month</TableCell>
// 											<TableCell sx={{ textAlign: 'center' }}>To Month</TableCell>
// 											<TableCell sx={{ textAlign: 'center' }}>Monthly Adjustment</TableCell>
// 											{/* <TableCell sx={{ textAlign: 'center' }}> Amount</TableCell> */}
// 											{screen !== 'VIEW' && screen !== 'EDIT' && <TableCell sx={{ textAlign: 'center' }}>Action</TableCell>}
// 										</TableRow>
// 									</TableHead>
// 									<TableBody>
// 										{fields.length > 0 ? (
// 											fields.map((field, index) => (
// 												<TableRow key={field.key}>
// 													<TableCell>
// 														<Form.Item name={[field.name, 'from']}>
// 															<Input
// 																disabled={true} // Always disabled to prevent manual editing
// 																style={{
// 																	padding: '8px',
// 																	width: '100%',
// 																	backgroundColor: '#f5f5f5', // Grey background to indicate disabled
// 																	textAlign: 'center',
// 																}}
// 															/>
// 														</Form.Item>
// 													</TableCell>

// 													<TableCell>
// 														<Form.Item
// 															name={[field.name, 'to']}
// 															rules={[
// 																{ required: false, message: "Please enter a 'To' value" },
// 																({ getFieldValue }) => ({
// 																	validator(_, value) {
// 																		const leaseType = getFieldValue('leaseType');
// 																		const fromValue = getFieldValue(['advanceAmountList', field.name, 'from']);
// 																		if (!value || !fromValue) return Promise.resolve();
// 																		if (parseInt(value) < parseInt(fromValue)) {
// 																			return Promise.reject(new Error("'To' must be greater than 'From'"));
// 																		}
// 																		if (leaseType === 'Operating Lease' && parseInt(value) > 12) {
// 																			return Promise.reject(new Error('Maximum month is 12'));
// 																		}
// 																		return Promise.resolve();
// 																	},
// 																}),
// 															]}
// 														>
// 															<Input
// 																type="number"
// 																disabled={screen === 'VIEW' || screen === 'EDIT'}
// 																placeholder="Enter To value"
// 																style={{
// 																	padding: '8px',
// 																	width: '100%',

// 																	textAlign: 'center',
// 																}}
// 																onChange={() => handleToChange(`${'advanceAmountList'}.${field.name}.to`)}
// 																onBlur={() => validateToValueOnBlur(index)}
// 															/>
// 														</Form.Item>
// 													</TableCell>
// 													<TableCell>
// 														<Form.Item
// 															name={[field.name, 'amount']}
// 															rules={[{ required: false, message: 'Please enter an amount' }]}
// 														>
// 															<NumericFormat
// 																size="small"
// 																decimalScale={2}
// 																placeholder="0.00"
// 																fixedDecimalScale
// 																thousandSeparator=","
// 																isAllowed={withValueCap}
// 																style={{
// 																	padding: '18px',
// 																	textAlign: 'right',
// 																	height: '32px',
// 																	borderRadius: '4px',
// 																	fontSize: '13px',
// 																}}
// 																customInput={Input}
// 																disabled={screen === 'VIEW' || screen === 'EDIT'}
// 																onValueChange={(values) => {
// 																	handleAmountChange(`${'advanceAmountList'}.${field.name}.amount`);
// 																	// First, get the current form values
// 																	const currentValues = [...form.getFieldValue('advanceAmountList')];

// 																	// Update the current field with the new value (or empty string if deleted)
// 																	if (currentValues[index]) {
// 																		// Update with the numeric value (not formatted)
// 																		currentValues[index].amount = values.value || '';

// 																		// Update the form with the new value
// 																		form.setFieldValue('advanceAmountList', currentValues);

// 																		// Calculate row total for this specific row
// 																		const fromValue = parseInt(currentValues[index].from);
// 																		const toValue = parseInt(currentValues[index].to);

// 																		// If amount is now empty, set row total to 0
// 																		if (!values.value) {
// 																			const newRowTotals = [...rowTotals];
// 																			newRowTotals[index] = 0;
// 																			setRowTotals(newRowTotals);

// 																			// Calculate new grand total from row totals
// 																			const newTotal = newRowTotals.reduce(
// 																				(total, rowTotal) => total + (rowTotal || 0),
// 																				0
// 																			);
// 																			setTotalAmount(newTotal);
// 																			return;
// 																		}

// 																		// Otherwise calculate normally
// 																		const amount = parseFloat(values.value);

// 																		if (
// 																			!isNaN(fromValue) &&
// 																			!isNaN(toValue) &&
// 																			!isNaN(amount) &&
// 																			toValue >= fromValue
// 																		) {
// 																			const newRowTotals = [...rowTotals];
// 																			newRowTotals[index] = (toValue - fromValue + 1) * amount;
// 																			setRowTotals(newRowTotals);

// 																			// Calculate new grand total from row totals
// 																			const newTotal = newRowTotals.reduce(
// 																				(total, rowTotal) => total + (rowTotal || 0),
// 																				0
// 																			);
// 																			setTotalAmount(newTotal);
// 																		}
// 																	}
// 																}}
// 																// Add an onBlur handler to ensure calculation happens
// 																onBlur={() => {
// 																	setTimeout(() => {
// 																		calculateRowTotals();
// 																		calculateTotal();
// 																		handleApply();
// 																	}, 10);
// 																}}
// 															/>
// 														</Form.Item>
// 													</TableCell>

// 													{/* <TableCell style={{ textAlign: 'right', fontWeight: 'bold' }}>
// 														<NumericFormat
// 															value={rowTotals[index] || 0}
// 															displayType="input"
// 															decimalScale={2}
// 															fixedDecimalScale
// 															thousandSeparator=","
// 															size="small"
// 															style={{
// 																padding: '18px',
// 																textAlign: 'right',
// 																height: '32px',
// 																borderRadius: '4px',
// 																fontSize: '13px',
// 																fontWeight: 'bold',
// 																backgroundColor: undefined,
// 																borderColor: undefined,
// 															}}
// 															customInput={Input}
// 															disabled={true}
// 															readOnly={true}
// 														/>
// 													</TableCell> */}

// 													{screen !== 'VIEW' && screen !== 'EDIT' && (
// 														<TableCell align="center">
// 															<>
// 																{/* Logic for displaying icons based on row position */}
// 																{/* First row with only one row in total - only plus icon */}
// 																{fields.length === 1 && index === 0 && (
// 																	<Tooltip title="Add Row">
// 																		<PlusCircleOutlined
// 																			style={{
// 																				fontSize: '20px',
// 																				color: '#1890ff',
// 																				cursor: 'pointer',
// 																			}}
// 																			onClick={() => handleAddRow(index)}
// 																		/>
// 																	</Tooltip>
// 																)}

// 																{/* Middle rows - only minus icon */}
// 																{fields.length > 1 && index > 0 && index < fields.length - 1 && (
// 																	<Tooltip title="Delete Row">
// 																		<MinusCircleOutlined
// 																			style={{
// 																				fontSize: '20px',
// 																				color: '#ff4d4f',
// 																				cursor: 'pointer',
// 																			}}
// 																			onClick={() => {
// 																				const currentValues = [...form.getFieldValue('advanceAmountList')];

// 																				// Update row totals array
// 																				const newRowTotals = [...rowTotals];
// 																				newRowTotals.splice(index, 1);
// 																				setRowTotals(newRowTotals);

// 																				// Remove the row
// 																				currentValues.splice(index, 1);

// 																				// Fix the From/To sequence if needed
// 																				for (let i = index; i < currentValues.length; i++) {
// 																					// If not the first row, From should be previous row's To + 1
// 																					if (i > 0) {
// 																						const prevTo = parseInt(currentValues[i - 1].to);
// 																						if (!isNaN(prevTo)) {
// 																							currentValues[i].from = prevTo + 1;
// 																						}
// 																					}
// 																				}

// 																				form.setFieldValue('advanceAmountList', currentValues);

// 																				// Calculate new total from row totals
// 																				const newTotal = newRowTotals.reduce(
// 																					(total, rowTotal) => total + (rowTotal || 0),
// 																					0
// 																				);
// 																				setTotalAmount(newTotal);
// 																				handleApply();
// 																			}}
// 																		/>
// 																	</Tooltip>
// 																)}

// 																{/* Last row (but not first) - both plus and minus icons */}
// 																{fields.length > 1 && index === fields.length - 1 && (
// 																	<>
// 																		<Tooltip title="Add Row">
// 																			<PlusCircleOutlined
// 																				style={{
// 																					fontSize: '20px',
// 																					color: '#1890ff',
// 																					marginRight: '12px',
// 																					cursor: 'pointer',
// 																				}}
// 																				onClick={() => handleAddRow(index)}
// 																			/>
// 																		</Tooltip>
// 																		<Tooltip title="Delete Row">
// 																			<MinusCircleOutlined
// 																				style={{
// 																					fontSize: '20px',
// 																					color: '#ff4d4f',
// 																					cursor: 'pointer',
// 																				}}
// 																				onClick={() => {
// 																					const currentValues = [
// 																						...form.getFieldValue('advanceAmountList'),
// 																					];

// 																					// Update row totals array
// 																					const newRowTotals = [...rowTotals];
// 																					newRowTotals.splice(index, 1);
// 																					setRowTotals(newRowTotals);

// 																					// Remove the row
// 																					currentValues.splice(index, 1);

// 																					// Fix the From/To sequence if needed
// 																					for (let i = index; i < currentValues.length; i++) {
// 																						// If not the first row, From should be previous row's To + 1
// 																						if (i > 0) {
// 																							const prevTo = parseInt(currentValues[i - 1].to);
// 																							if (!isNaN(prevTo)) {
// 																								currentValues[i].from = prevTo + 1;
// 																							}
// 																						}
// 																					}

// 																					form.setFieldValue('advanceAmountList', currentValues);

// 																					// Calculate new total from row totals
// 																					const newTotal = newRowTotals.reduce(
// 																						(total, rowTotal) => total + (rowTotal || 0),
// 																						0
// 																					);
// 																					setTotalAmount(newTotal);
// 																					handleApply();
// 																				}}
// 																			/>
// 																		</Tooltip>
// 																	</>
// 																)}

// 																{/* First row with multiple rows - no icons */}
// 																{fields.length > 1 && index === 0 && <span></span>}
// 															</>
// 														</TableCell>
// 													)}
// 												</TableRow>
// 											))
// 										) : (
// 											<TableRow>
// 												<TableCell colSpan={5} style={{ textAlign: 'center' }}>
// 													No data. Click the plus icon to add new entries.
// 												</TableCell>
// 											</TableRow>
// 										)}
// 									</TableBody>
// 									<TableFooter>
// 										<TableRow sx={{ height: 24 }}>
// 											<TableCell colSpan={2} align="right" style={{ fontWeight: 'bold' }}>
// 												<InputLabel
// 													label={'Advance Payment'}
// 													head={true}
// 													sx={{
// 														justifyContent: 'end',
// 														marginBottom: '0px',
// 													}}
// 												/>
// 											</TableCell>
// 											<TableCell style={{ fontWeight: 'bold', textAlign: 'right' }}>
// 												<NumericFormat
// 													value={totalAmount || 0}
// 													displayType="input"
// 													decimalScale={2}
// 													fixedDecimalScale
// 													thousandSeparator=","
// 													size="small"
// 													style={{
// 														padding: '18px',
// 														textAlign: 'right',
// 														height: '32px',
// 														borderRadius: '4px',
// 														fontSize: '13px',
// 														fontWeight: 'bold',
// 														backgroundColor: undefined,
// 														borderColor: undefined,
// 													}}
// 													customInput={Input}
// 													disabled={true}
// 													readOnly={true}
// 												/>
// 											</TableCell>
// 											{screen !== 'VIEW' && screen !== 'EDIT' && <TableCell colSpan={1}></TableCell>}
// 										</TableRow>
// 									</TableFooter>
// 								</Table>
// 							</TableContainer>

// 							{/* Hidden form field to store processed data for API submission */}
// 							<Form.Item name="advanceAmounts" noStyle />
// 						</>
// 					);
// 				}}
// 			</Form.List>

// 			<Modal
// 				title={'Advance Amount Modifications'}
// 				open={openModal}
// 				onCancel={() => setOpenModal(false)}
// 				width={800}
// 				footer={[
// 					<Button key="cancel" onClick={() => setOpenModal(false)}>
// 						Close
// 					</Button>,
// 				]}
// 			>
// 				{advanceAmountListSetForHistory?.map((item, index) => (
// 					<AdvanceAmountHistory key={index} data={item} index={index} />
// 				))}
// 			</Modal>
// 		</div>
// 	);
// };

// export default AdvanceAmount;
